/**
 * Which scripts run for a request under Run collection, in Postman's order:
 * collection → folders (outermost first) → the request itself, for both the
 * pre-request and the test phase.
 *
 * Folder scripts come from the Postman importer (`settings.folders[].
 * preRequestScript` / `.testScript`, parsePostman `withScripts`); collection
 * scripts from `settings.preRequestScript` / `.testScript` (same importer,
 * 26.09.2026). Before this they were imported and never executed, so an imported
 * collection whose token flow lives in a collection-level pre-request script
 * ran every request without its token. Card 77f785cc.
 */
import { getFolders, normalizeParentId } from './collectionFolders';

const FIELD = { pre: ['preRequestScript', 'pre_request_script'], test: ['testScript', 'test_script'] };

function scriptOf(node, phase) {
  if (!node) return '';
  const [camel, snake] = FIELD[phase];
  const text = node[camel] ?? node[snake] ?? '';
  return typeof text === 'string' ? text : '';
}

/** Folders enclosing `folderId`, OUTERMOST first. Cycle-safe. */
function folderPath(collection, folderId) {
  const byId = new Map(getFolders(collection).map((f) => [String(f.id), f]));
  const path = [];
  const seen = new Set();
  let current = normalizeParentId(folderId);
  while (current && !seen.has(current)) {
    seen.add(current);
    const folder = byId.get(current);
    if (!folder) break;
    path.unshift(folder);
    current = normalizeParentId(folder.parent_id);
  }
  return path;
}

/**
 * @param {object} collection
 * @param {object} request  sidebar/editor request (reads folder_id and its scripts)
 * @param {'pre'|'test'} phase
 * @returns {{ level: 'collection'|'folder'|'request', script: string }[]} non-empty scripts only
 */
export function scriptChainFor(collection, request, phase) {
  const chain = [
    { level: 'collection', script: scriptOf(collection?.settings, phase) },
    ...folderPath(collection, request?.folder_id ?? request?.folderId).map((f) => ({
      level: 'folder',
      script: scriptOf(f, phase),
    })),
    { level: 'request', script: scriptOf(request, phase) },
  ];
  return chain.filter((s) => s.script.trim());
}
